import { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import useUser from 'src/hooks/use-user';
import useCart from 'hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import { useRouter } from 'next/compat/router';
import { useLoyaltyModal } from 'src/components/modals/loyalty';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { getAiqSmsPinTriggerDecision } from 'shared/helpers/rewards';
import { GetDutchiePosLoyaltyConfigDocument } from 'src/gql/graphql';
import {
  detectLoyaltyIntegration,
  Integrations,
  getIsDutchiePosLoyaltyEnrollmentEligible,
} from '../loyalty/use-loyalty';

const PUBLIC_SESSION_PATHS = [
  '/login/public-session-login',
  '/login/public-session-continue',
  '/login/public-session-phone-continue',
];

// DutchiePay enrollment routes own the screen with a Plaid modal — don't let
// the loyalty modal auto-open on top of it. Mirrors PUBLIC_SESSION_PATHS.
const PAY_ENROLLMENT_PATHS = ['/pay/signup', '/pay/instore'];
const MAX_TRACKED_AUTO_POPUP_CONTEXTS = 100;

const addTrackedContext = (contexts: Set<string>, key: string): void => {
  contexts.delete(key);
  contexts.add(key);
  if (contexts.size > MAX_TRACKED_AUTO_POPUP_CONTEXTS) {
    const oldestKey = contexts.values().next().value;
    if (oldestKey) {
      contexts.delete(oldestKey);
    }
  }
};

export const LoyaltyAccountDetection = ({ children }): JSX.Element => {
  const { isEmbedded, isStoreFront, isEmbeddedCarousel } = useUI();
  const isEmbeddedOrStoreFront = (isEmbedded || isStoreFront) && !isEmbeddedCarousel;
  const User = useUser();
  const Cart = useCart();
  const cartSubtotal = useObserver(() => Cart.chargeSubtotal);
  const router = useRouter();
  const isUserLoading = useObserver(() => User.loading);
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const isUserLoadedAndLoggedIn = !isUserLoading && isLoggedIn;
  const userId = useObserver(() => User.id);
  const phone = useObserver(() => User.phone);
  const phoneVerifiedAt = useObserver(() => User.phoneVerifiedAt);
  const birthday = useObserver(() => User.birthday);
  const postSignupModalSequenceDispensaryId = useObserver(() => User.postSignupModalSequenceDispensaryId);
  const { dispensary } = useDispensary();
  const loyaltyProgram = detectLoyaltyIntegration(dispensary);
  const dispensaryId = dispensary?.id;
  const isPostSignupModalSequenceActiveForDispensary =
    !!dispensaryId && postSignupModalSequenceDispensaryId === dispensaryId;
  const pin = User.getLoyaltyPin(dispensaryId);
  const flags = useFlags();
  const isEnrollmentPhoneVerificationGateEnabled =
    flags['pos.ecomm.loyalty-enrollment-verified-phone.rollout'] ?? false;
  const isKioskProEnabled = useKioskPro();
  const isShowLoyaltyFlagEnabled = (dispensary?.storeSettings?.showLoyaltyRewards ?? false) || isKioskProEnabled;
  const isShowSpringbigLoyaltyFlagEnabled = flags['growth.ecomm.springbig-member-signup.rollout'] ?? false;
  // Staged migration off the legacy pos.customers.skip-sms-sending.rollout flag (POS-21141).
  // Once pos.customers.aiq-sms-pin-trigger-setting.rollout is on for this dispensary, the
  // dispensary's own aiqSmsPinTriggerBehavior setting takes over; until then this preserves
  // today's behavior exactly.
  const isAiqSmsPinTriggerSettingEnabled = flags['pos.customers.aiq-sms-pin-trigger-setting.rollout'] ?? false;
  const isSkipSMSSendingEnabled = flags['pos.customers.skip-sms-sending.rollout'] ?? false;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const hasSessionId = Boolean(router?.query?.['session-id']);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const isPublicSessionPage = PUBLIC_SESSION_PATHS.some((path) => router?.pathname?.includes(path)) || hasSessionId;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const isPayEnrollmentPage = PAY_ENROLLMENT_PATHS.some((path) => router?.pathname?.includes(path));
  const showKioskProLoyalty = isKioskProEnabled && !isPublicSessionPage;
  const showSpringbigLoyalty = loyaltyProgram === Integrations.springbig ? isShowSpringbigLoyaltyFlagEnabled : true;
  const isAlpineIQEnabled = loyaltyProgram === Integrations.alpineiq;
  const aiqSmsPinTriggerDecision = useObserver(() =>
    getAiqSmsPinTriggerDecision({
      isAiqSmsPinTriggerSettingEnabled,
      legacyShouldSkipSmsPin: isSkipSMSSendingEnabled,
      dispensaryId,
      userId,
      readiness: Cart.aiqSmsPinTriggerSettingsReadiness,
      aiqSmsPinTriggerBehavior: Cart.dispensary?.storeSettings?.aiqSmsPinTriggerBehavior,
      userEnteredPin: pin,
    })
  );
  const { shouldSkipSmsPin } = aiqSmsPinTriggerDecision;
  const { isOpen: isLoyaltyModalOpen, open: showLoyaltyModal } = useLoyaltyModal();
  // Auto-popup once per shopper+dispensary; rewards refetches (e.g. cart subtotal) must not reopen it.
  const autoPopupShownForRef = useRef<Set<string>>(new Set());
  // Don't pop over the flagged inline add-number flow.
  const lastPhoneStateRef = useRef<{ key: string | null; hasPhone: boolean; isVerified: boolean } | null>(null);
  const phoneAddedForRef = useRef<Set<string>>(new Set());
  const skip = isAlpineIQEnabled || !phone || !birthday || !dispensaryId || !userId;

  const { data, loading } = useGetRewards({
    phone,
    dispensaryId,
    birthday,
    pin: shouldSkipSmsPin && isAlpineIQEnabled ? 'skip' : pin,
    skip,
    customerId: userId,
    automaticAiqRequest: {
      cart: Cart,
      enabled: isAiqSmsPinTriggerSettingEnabled && !pin,
      isAlpineIQEnabled,
      userId: userId ?? null,
    },
    cartSubtotal,
  });

  const { data: dposConfigData, loading: dposConfigLoading } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: {
      dispensaryId,
    },
    skip: loyaltyProgram !== Integrations.dutchiepos,
  });

  const phoneContextKey = userId && dispensaryId ? `${userId}:${dispensaryId}` : null;
  const shouldSuppressPhoneAddedPopup =
    isEnrollmentPhoneVerificationGateEnabled && loyaltyProgram === Integrations.dutchiepos;

  useEffect(() => {
    const previousPhoneState = lastPhoneStateRef.current;
    if (
      shouldSuppressPhoneAddedPopup &&
      previousPhoneState?.key === phoneContextKey &&
      !previousPhoneState.hasPhone &&
      phone &&
      phoneContextKey
    ) {
      addTrackedContext(phoneAddedForRef.current, phoneContextKey);
    }

    const isPhoneVerified = Boolean(phoneVerifiedAt);
    const wasVerifiedOutsideLoyaltyFlow =
      shouldSuppressPhoneAddedPopup &&
      previousPhoneState?.key === phoneContextKey &&
      !previousPhoneState.isVerified &&
      isPhoneVerified &&
      !isLoyaltyModalOpen;
    if (wasVerifiedOutsideLoyaltyFlow && phoneContextKey) {
      phoneAddedForRef.current.delete(phoneContextKey);
      autoPopupShownForRef.current.delete(phoneContextKey);
    }
    lastPhoneStateRef.current = { key: phoneContextKey, hasPhone: Boolean(phone), isVerified: isPhoneVerified };

    const showForAlpine = loyaltyProgram === Integrations.alpineiq && !pin;
    const showForSpringbig =
      loyaltyProgram === Integrations.springbig && !loading && !data?.getRewardsV2?.userHasWallet;
    const showForDutchiePos =
      !dposConfigLoading &&
      !loading &&
      getIsDutchiePosLoyaltyEnrollmentEligible({
        showLoyaltyRewards: isShowLoyaltyFlagEnabled,
        loyaltyProgram,
        loyaltyConfig: dposConfigData?.getDutchiePosLoyaltyConfig,
        isLoyaltyMember: data?.getRewardsV2?.isLoyaltyMember,
      });

    if (
      (isEmbeddedOrStoreFront || showKioskProLoyalty) &&
      !isPayEnrollmentPage &&
      !isPostSignupModalSequenceActiveForDispensary &&
      isUserLoadedAndLoggedIn &&
      [Integrations.alpineiq, Integrations.springbig, Integrations.dutchiepos].includes(loyaltyProgram) &&
      showSpringbigLoyalty &&
      dispensaryId &&
      !pin &&
      isShowLoyaltyFlagEnabled &&
      phone &&
      birthday &&
      userId &&
      (showForAlpine || showForSpringbig || showForDutchiePos) &&
      phoneContextKey &&
      (!shouldSuppressPhoneAddedPopup || !phoneAddedForRef.current.has(phoneContextKey)) &&
      !autoPopupShownForRef.current.has(phoneContextKey)
    ) {
      addTrackedContext(autoPopupShownForRef.current, phoneContextKey);
      void showLoyaltyModal({ surface: 'autoPopup' });
    }
  }, [
    dispensaryId,
    loyaltyProgram,
    showSpringbigLoyalty,
    shouldSuppressPhoneAddedPopup,
    phoneContextKey,
    phoneVerifiedAt,
    isLoyaltyModalOpen,
    isEmbeddedOrStoreFront,
    isShowLoyaltyFlagEnabled,
    isUserLoadedAndLoggedIn,
    phone,
    birthday,
    userId,
    pin,
    showLoyaltyModal,
    data,
    loading,
    dposConfigData,
    dposConfigLoading,
    showKioskProLoyalty,
    isPayEnrollmentPage,
    isPostSignupModalSequenceActiveForDispensary,
  ]);

  return children;
};
